import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ListX, ScanSearch, FilePlus2, FileStack,
  Users, ShieldCheck, Building2, LogOut, Menu, X, Skull, ShieldAlert,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import Footer from "@/components/Footer";
import Watermark from "@/components/Watermark";
import SecurityGuard from "@/components/SecurityGuard";
import { KeyRound } from "lucide-react";

const NAV = {
  root: [
    { to: "/", label: "Tổng quan", icon: LayoutDashboard },
    { to: "/danh-sach-den", label: "Danh sách đen", icon: ListX },
    { to: "/tra-cuu", label: "Tra cứu nhanh", icon: ScanSearch },
    { to: "/them-danh-sach", label: "Thêm danh sách đen", icon: FilePlus2 },
    { to: "/duyet-bao-cao", label: "Duyệt báo cáo", icon: ShieldCheck },
    { to: "/duyet-nha-xe", label: "Duyệt nhà xe", icon: Building2 },
    { to: "/nhat-ky-bao-mat", label: "Nhật ký bảo mật", icon: ShieldAlert },
  ],
  company: [
    { to: "/", label: "Tổng quan", icon: LayoutDashboard },
    { to: "/danh-sach-den", label: "Danh sách đen", icon: ListX },
    { to: "/tra-cuu", label: "Tra cứu nhanh", icon: ScanSearch },
    { to: "/bao-cao", label: "Gửi báo cáo", icon: FilePlus2 },
    { to: "/bao-cao-cua-toi", label: "Báo cáo của tôi", icon: FileStack },
    { to: "/nhan-vien", label: "Nhân viên", icon: Users },
  ],
  employee: [
    { to: "/", label: "Tổng quan", icon: LayoutDashboard },
    { to: "/danh-sach-den", label: "Danh sách đen", icon: ListX },
    { to: "/tra-cuu", label: "Tra cứu nhanh", icon: ScanSearch },
  ],
};

const ROLE_LABEL = { root: "Tài khoản gốc", company: "Nhà xe", employee: "Nhân viên" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavList = () => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const active = location.pathname === it.to;
        const Icon = it.icon;
        return (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`nav-${it.to === "/" ? "home" : it.to.slice(1)}`}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors duration-150 border-l-2 ${
              active
                ? "bg-primary/10 text-white border-primary"
                : "text-zinc-400 border-transparent hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {it.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <Watermark />
      <SecurityGuard />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-zinc-800 bg-[#0a0a0a] fixed h-screen">
        <div className="flex items-center gap-3 px-6 h-20 border-b border-zinc-800">
          <div className="w-10 h-10 bg-primary flex items-center justify-center shadow-[0_0_15px_rgba(230,0,0,0.4)]">
            <Skull className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-display font-black text-lg leading-none tracking-tight text-white">
              DANH SÁCH ĐEN
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              Cảnh báo khách rủi ro
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-4 px-3">
          <NavList />
        </div>
        <div className="border-t border-zinc-800 p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">
                {user?.company_name || user?.name}
              </div>
              <div className="text-xs text-zinc-500">
                {ROLE_LABEL[user?.role]} · {user?.phone}
              </div>
            </div>
            {user?.role === "company" && <NotificationBell />}
          </div>
          <ChangePasswordDialog
            trigger={
              <button
                data-testid="change-password-btn"
                className="w-full mb-2 flex items-center justify-center gap-2 bg-transparent border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary transition-colors duration-150 rounded-sm px-4 py-2 text-sm"
              >
                <KeyRound className="w-4 h-4" /> Đổi mật khẩu
              </button>
            }
          />
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-transparent border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary transition-colors duration-150 rounded-sm px-4 py-2 text-sm"
          >
            <LogOut className="w-4 h-4" /> Đăng xuất
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-zinc-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary flex items-center justify-center">
            <Skull className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-black text-white tracking-tight">DANH SÁCH ĐEN</span>
        </div>
        <div className="flex items-center gap-1">
        {user?.role === "company" && <NotificationBell />}
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="text-white p-2">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        </div>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-30 pt-16 bg-black/95 backdrop-blur-xl">
          <div className="p-4">
            <NavList />
            <div className="mt-6 border-t border-zinc-800 pt-4">
              <div className="text-sm font-semibold text-white">{user?.company_name || user?.name}</div>
              <div className="text-xs text-zinc-500 mb-3">{ROLE_LABEL[user?.role]} · {user?.phone}</div>
              <ChangePasswordDialog
                trigger={
                  <button
                    data-testid="change-password-btn-mobile"
                    className="w-full mb-2 flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 rounded-sm px-4 py-2 text-sm"
                  >
                    <KeyRound className="w-4 h-4" /> Đổi mật khẩu
                  </button>
                }
              />
              <button
                data-testid="logout-btn-mobile"
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 rounded-sm px-4 py-2 text-sm"
              >
                <LogOut className="w-4 h-4" /> Đăng xuất
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 min-h-screen noise-bg flex flex-col">
        <div className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-10 animate-fade-up">{children}</div>
        <Footer />
      </main>
    </div>
  );
}
